import { sendErrorMetric } from "@expert/cloudwatch-metrics";
import { getLogger } from "@expert/logging";
import { Container, Flex, Text } from "@mantine/core";
import { type JSX, useEffect } from "react";
import { useCallDetailsStore } from "@/call-details";
import { useVerifiedCustomerStore } from "@/customer-info";
import { usePartnerFeatureConfigured } from "@/partner/usePartnerConfig";
import { useEligibility } from "@/sales-tools/home-product/hooks/useEligibility";
import { useProductOfferPolling } from "@/sales-tools/home-product/hooks/useProductOfferPolling";
import { MultiProduct } from "@/sales-tools/multi-product/MultiProduct";
import { UnifiedSalesView } from "@/sales-tools/unified-sales-view/UnifiedSalesView";
import { usePartner, useSessionId, useTypedMostRecentTask } from "@/sdk";
import { useProductOfferStore } from "./home-product";
import { IneligibleIcon, LoadingAnimation } from "./home-product/components/Icons";
import { useQuerySelectedProductOffer } from "./home-product/hooks/useQuerySelectedProductOffer";
import classes from "./SalesFlow.module.css";
import { useAgentAttributesStore } from "./stores/agent-attributes.store";
import { getReservationSid, parsePhoneNumberAttributes } from "./utils";

const logger = getLogger({
    module: "SalesFlow",
    supportTeam: "sales",
});

export function SalesFlow(): JSX.Element {
    useQuerySelectedProductOffer();
    useProductOfferPolling();

    const {
        task: mostRecentTask,
        isVoice,
        isChat,
        voiceTask,
        chatTask,
        customerInteractionId,
    } = useTypedMostRecentTask();
    const partner = usePartner();

    if (!isVoice && !isChat) {
        throw new Error("Active task is not voice or chat task");
    }
    const { isUnifiedSalesViewEnabled } = usePartnerFeatureConfigured();

    const { CLIENT, PROTECTED_SALES } = useCallDetailsStore.getState().callDetails;
    const showUnifiedSalesView = isUnifiedSalesViewEnabled || isChat || CLIENT === "ATT_IBC";

    const sessionId = useSessionId();
    const customer = useVerifiedCustomerStore((state) => state.getVerifiedCustomer(sessionId).customer);
    const storesHaveFinishedRehydrating = useProductOfferStore.persist.hasHydrated();
    const storesHaveRehydratedData = useProductOfferStore((state) => (state.getProducts(sessionId)?.length ?? 0) > 0);
    const isCustomerAccountNumberSet = Boolean(customer?.accountNumber);
    const hasCurrentSubscriptions = (customer?.currentSubscriptions?.length ?? 0) > 0;
    const shouldRefetchEligibilityForSession =
        (storesHaveFinishedRehydrating && !storesHaveRehydratedData) ||
        isCustomerAccountNumberSet ||
        hasCurrentSubscriptions;

    const isCustomerInProtectedState = PROTECTED_SALES === "1";
    const {
        data: checkEligibilityResult,
        error,
        isError: eligibilityError,
        isLoading: isLoadingEligibility,
    } = useEligibility(
        {
            // TODO: remove `asurionCallId` after https://github.com/Soluto-Private/expert-workspace-api/pull/2240 is deployed to prod
            asurionCallId: isVoice && voiceTask ? voiceTask.asurionCallId : undefined,
            customer: {
                accountNumber: customer?.accountNumber || "",
            },
            customerInteractionId,
            partner,
            reservationSid: getReservationSid(mostRecentTask) ?? sessionId,
            sessionId,
            ...parsePhoneNumberAttributes(customer?.mdn),
            address: customer?.address,
            eligibilityAttributes: {
                currentPlan: customer?.productName,
                isInternationalCaller: customer?.metadata?.isInternationalCaller,
                chatSourceApp: isChat ? chatTask?.sourceApp : undefined,
                currentSubscriptions: customer?.currentSubscriptions,
                expertLocation: useAgentAttributesStore.getState().site,
                isCustomerInProtectedState,
                client: CLIENT,
            },
            salesChannel: mostRecentTask?.type,
        },
        shouldRefetchEligibilityForSession,
    );

    useEffect(() => {
        if (shouldRefetchEligibilityForSession && checkEligibilityResult) {
            useProductOfferStore.getState().updateProducts(sessionId, checkEligibilityResult.productConfigs);
        }
    }, [checkEligibilityResult, sessionId, shouldRefetchEligibilityForSession]);

    if (eligibilityError) {
        logger.error({ error }, `Error checking eligibility FE`);
        void sendErrorMetric("EligibilityError", { eventSource: "Eligibility-FE" });
        return (
            <Flex className={classes.loadingContainer} data-testid="failure">
                <Container>{IneligibleIcon}</Container>
                <Text className={classes.statusBodyHeader}>We had an issue checking eligibility.</Text>
                <Text className={classes.statusBodyText} data-testid="sale-view-status-body">
                    Try refreshing the page.
                </Text>
            </Flex>
        );
    }

    if (isLoadingEligibility) {
        return (
            <Flex className={classes.loadingContainer}>
                <Text size="sm">Confirming customer information...</Text>
                <Container maw="160px">
                    <LoadingAnimation />
                </Container>
            </Flex>
        );
    }

    return showUnifiedSalesView ? <UnifiedSalesView isChat={isChat} /> : <MultiProduct />;
}
